// src/pages/MySettingsPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Space,
  Button,
  Form,
  Input,
  Tag,
  Alert,
  Table,
  Modal,
  message,
  Spin,
  Divider,
  Segmented,
} from "antd";
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  SafetyOutlined,
  LaptopOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../api/axiosInstance";

const { Title, Text } = Typography;

const fmtUtc = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
};

const clip = (s, n = 48) => {
  if (!s) return "—";
  return s.length > n ? s.slice(0, n) + "…" : s;
};

const maskHash = (hash) => {
  if (!hash) return "—";
  if (hash.length <= 10) return hash;
  return `${hash.slice(0, 6)}…${hash.slice(-4)}`;
};

const MySettingsPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [data, setData] = useState({
    current: null, // { id?, deviceHashMasked?, isTrusted, deviceLabel, userAgent, lastSeenUtc }
    devices: [], // list of devices
  });

  const [view, setView] = useState("Trusted"); // Trusted | All

  const [form] = Form.useForm();
  const initialLabelRef = useRef("");

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      // ✅ API: [Route("api/devices")] + [HttpGet("summary")]
      const res = await axiosInstance.get("/devices/summary");

      const current = res.data?.current ?? null;
      const devices = Array.isArray(res.data?.devices) ? res.data.devices : [];

      setData({ current, devices });

      const label = current?.deviceLabel ?? "";
      initialLabelRef.current = label;
      form.setFieldsValue({ deviceLabel: label });
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to load device settings.");
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const currentTrusted = !!data.current?.isTrusted;

  const devicesForTable = useMemo(() => {
    const list = data.devices || [];
    if (view === "Trusted") return list.filter((d) => d.isTrusted);
    return list;
  }, [data.devices, view]);

  const onRegisterThisDevice = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // ✅ API: [HttpPost("register")]
      await axiosInstance.post("/devices/register", {
        deviceLabel: values.deviceLabel || null,
      });

      message.success("This device has been registered (trusted).");
      await fetchDevices();
    } catch (err) {
      if (err?.errorFields) return; // form validation
      message.error(err?.response?.data?.message || "Failed to register device.");
    } finally {
      setSaving(false);
    }
  };

  const onRevoke = (row) => {
    Modal.confirm({
      title: "Revoke device trust?",
      icon: <ExclamationCircleOutlined />,
      content: "This will mark the device as untrusted. The user may be asked to register it again later.",
      okText: "Revoke",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setSaving(true);
          // ✅ API: [HttpPost("{id:long}/revoke")]
          await axiosInstance.post(`/devices/${row.id}/revoke`);
          message.success("Device trust revoked.");
          await fetchDevices();
        } catch (err) {
          message.error(err?.response?.data?.message || "Failed to revoke device.");
        } finally {
          setSaving(false);
        }
      },
    });
  };

  const columns = [
    {
      title: "Label",
      dataIndex: "deviceLabel",
      key: "deviceLabel",
      render: (v) => <Text>{v || "—"}</Text>,
    },
    {
      title: "User Agent",
      dataIndex: "userAgent",
      key: "userAgent",
      render: (v) => <Text type="secondary">{clip(v, 64)}</Text>,
    },
    {
      title: "Last Seen (UTC)",
      dataIndex: "lastSeenUtc",
      key: "lastSeenUtc",
      width: 200,
      render: (v) => <Text>{fmtUtc(v)}</Text>,
    },
    {
      title: "Status",
      dataIndex: "isTrusted",
      key: "isTrusted",
      width: 120,
      render: (v) => (v ? <Tag color="green">Trusted</Tag> : <Tag color="red">Untrusted</Tag>),
    },
    {
      title: "Action",
      key: "action",
      width: 140,
      render: (_, row) => (
        <Button danger size="small" onClick={() => onRevoke(row)} disabled={!row.isTrusted || saving}>
          Revoke
        </Button>
      ),
    },
  ];

  const currentHashDisplay =
    data.current?.deviceHashMasked || maskHash(data.current?.deviceHash);

  const labelValue = Form.useWatch("deviceLabel", form);
  const labelChanged = (labelValue ?? "") !== (initialLabelRef.current ?? "");
  const canRegister = !currentTrusted || labelChanged;

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: "100%", justifyContent: "space-between" }} align="center">
        <Space align="center">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            My Settings
          </Title>
        </Space>

        <Button icon={<ReloadOutlined />} onClick={fetchDevices} disabled={loading || saving}>
          Refresh
        </Button>
      </Space>

      <div style={{ height: 12 }} />

      <Spin spinning={loading}>
        <Card
          title={
            <Space>
              <SafetyOutlined />
              <span>Device Registration</span>
            </Space>
          }
        >
          {!data.current ? (
            <Alert
              type="warning"
              showIcon
              title="Unable to determine current device."
              description='If your app is on a different domain than the API, ensure cookies/credentials are enabled so the server can identify the device.'
            />
          ) : (
            <>
              <Alert
                type={currentTrusted ? "success" : "warning"}
                showIcon
                title={currentTrusted ? "This device is trusted." : "This device is not trusted yet."}
                description={
                  currentTrusted
                    ? "Your account will recognize this device on future sign-ins."
                    : "Registering this device helps reduce security warnings and can be used for device-based policies."
                }
              />

              <Divider />

              <Space align="start" style={{ width: "100%" }} size="large" wrap>
                <Card size="small" style={{ minWidth: 320, flex: 1 }} title="This device">
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Space>
                      <LaptopOutlined />
                      <Text strong>Current Device</Text>
                      {currentTrusted ? <Tag color="green">Trusted</Tag> : <Tag color="orange">Not trusted</Tag>}
                    </Space>

                    <Text type="secondary">
                      Device Hash: <Text code>{currentHashDisplay}</Text>
                    </Text>

                    <Text type="secondary">
                      User Agent: <Text>{clip(data.current.userAgent, 90)}</Text>
                    </Text>

                    <Text type="secondary">
                      Last Seen: <Text>{fmtUtc(data.current.lastSeenUtc)}</Text>
                    </Text>

                    <Form form={form} layout="vertical">
                      <Form.Item
                        label="Device label (optional)"
                        name="deviceLabel"
                        rules={[{ max: 256, message: "Max 256 characters." }]}
                      >
                        <Input placeholder='e.g. "Front Desk PC" or "My Laptop"' allowClear />
                      </Form.Item>

                      <Space align="center" wrap>
                        <Button type="primary" onClick={onRegisterThisDevice} loading={saving} disabled={!canRegister}>
                          Register this device
                        </Button>

                        {currentTrusted && (
                          <Text type="secondary">Tip: change the label and click Register again to save it.</Text>
                        )}
                      </Space>
                    </Form>
                  </Space>
                </Card>

                <Card
                  size="small"
                  style={{ minWidth: 380, flex: 2 }}
                  title={
                    <Space style={{ width: "100%", justifyContent: "space-between" }} align="center">
                      <span>Devices</span>
                      <Segmented value={view} onChange={(v) => setView(v)} options={["Trusted", "All"]} />
                    </Space>
                  }
                >
                  <Table
                    rowKey="id"
                    size="small"
                    columns={columns}
                    dataSource={devicesForTable}
                    pagination={{ pageSize: 6 }}
                    locale={{
                      emptyText: view === "Trusted" ? "No trusted devices yet." : "No devices found.",
                    }}
                  />
                </Card>
              </Space>
            </>
          )}
        </Card>
      </Spin>
    </div>
  );
};

export default MySettingsPage;
